import React, { useState } from 'react';
import moment from 'moment';
import html2pdf from 'html2pdf.js';
import logo from '../../../assests/images/logo.png';
import FormFooter from './FormFooter';



/**
 * SblcSection — SBLC (Stand By Letter of Credit) letter document.
 *
 * Renders a printable A4-sized SBLC letter with:
 *  - Company logo + file name header
 *  - Letter No / Date row
 *  - Addressee block (Branch Manager, South Indian Bank)
 *  - Details table (Bill Ref, Drawer, Drawee, Currency, Due Date, Tenor)
 *  - Standard body paragraphs
 *  - Download PDF button (html2pdf)
 *
 * Props:
 *   retrievedData {object}  Data entry record from useDataEntry()
 */

// ── Constants ─────────────────────────────────────────────────────────────────
const SECTION_ID = 'bank_section_1';
const PDF_FILENAME = 'SBLC-Details.pdf';

const DRAWEE_NAME = 'BEST TIMBERS';

const PDF_OPTIONS = {
  margin: 0,
  filename: PDF_FILENAME,
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    scrollY: 0,
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: document.documentElement.scrollHeight,
  },
  jsPDF: {
    unit: 'mm',
    format: [210, 350],
    orientation: 'portrait',
  },
  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
};

// ── Styles (defined outside component — no re-creation on each render) ────────
const styles = {
  container: {
    width: '100%',
    maxWidth: '794px',
    minHeight: '1123px',
    background: 'white',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
    marginBottom: '8px',

  },
  logo: {
    height: 'auto',
    width: '28%',
    objectFit: 'contain',
  },
  fileName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a56db',
    margin: 0,
    letterSpacing: '1px',
  },
  text: {
    fontSize: '13px',
    margin: '2px 0',
  },
  textSpaced: {
    fontSize: '13px',
    margin: '6px 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '8px 0',
    fontSize: '13px',
  },
  tdLabel: {
    border: '1px solid #000',
    padding: '8px 12px',
    width: '45%',
  },
  tdValue: {
    border: '1px solid #000',
    padding: '8px 12px',
  },
  signoff: {
    fontSize: '13px',
    fontWeight: '600',
    marginTop: '32px',
    marginBottom: '4px',
  },
  partner: {
    fontSize: '13px',
    marginTop: '32px',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
const SblcSection = ({ retrievedData }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDownload = () => {
    const element = document.getElementById(SECTION_ID);
    if (!element) return;

    setIsProcessing(true);
    html2pdf()
      .set(PDF_OPTIONS)
      .from(element)
      .save()
      .then(() => setIsProcessing(false))
      .catch(() => setIsProcessing(false));
  };

  return (
    <>
      {/* ── Printable Document Area ─────────────────────────────────────── */}
      <div id={SECTION_ID} style={styles.container}>
        <div className="px-4 my-4">

          {/* Logo + File Name Header */}
          <div className="status-logo py-1">
            <img
              src={logo}
              alt="logo"
              style={{ height: '90%', width: '30%', objectFit: 'contain' }}
            />

            <p className="file-name-title">
              {retrievedData && retrievedData?.fileName}
            </p>
          </div>


          {/* Letter Body */}
          <div className="d-flex flex-column py-2">

            {/* Letter No. + Date */}
            <div className="d-flex justify-content-between py-2">
              <p style={styles.text}>Letter No.</p>
              <p style={styles.text}>Date : {moment().format('DD-MM-YYYY')}</p>
            </div>

            {/* Addressee */}
            <div className="d-flex flex-column py-2">
              <p style={styles.text}>To</p>
              <p style={styles.text}>The Branch Manager</p>
              <p style={styles.text}>Branch :</p>
              <p style={styles.text}>The South Indian Bank Ltd</p>
            </div>

            {/* Subject */}
            <div className="d-flex flex-column py-2">
              <p style={styles.text}>Sir/Madam</p>
              <p style={{ ...styles.text, margin: '4px 0' }}>
                Sub: We request you to issue/rollover trade credit through stand by letter of
                credit on our behalf for the following outstanding import bill,
              </p>
            </div>

            {/* SBLC Details Table */}
            <table style={styles.table}>
              <tbody>
                <tr>
                  <td style={styles.tdLabel}>Bill Reference No:</td>
                  <td style={styles.tdValue}>{retrievedData?.blNo || ''}</td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Drawer:</td>
                  <td style={styles.tdValue}>{retrievedData?.exporter || ''}</td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Drawee:</td>
                  <td style={styles.tdValue}>{DRAWEE_NAME}</td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Currency and Amount:</td>
                  <td style={styles.tdValue}>
                    USD {retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : ''}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Bill Due Date:</td>
                  <td style={styles.tdValue}>
                    {/* Bill due date — to be added */}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Tenor</td>
                  <td style={styles.tdValue}>180 DAYS</td>
                </tr>
              </tbody>
            </table>

            {/* Body Paragraphs */}
            <p style={styles.textSpaced}>
              Please process the trade credit request and arrange to send your stand by letter
              of credit to <u>{retrievedData?.foreignBankName || ''}</u> Bank and confirm.
            </p>
            <p style={styles.textSpaced}>
              We hereby agree with all the terms and conditions of stand by letter of credit
              which will be issued by you to the lending party on our behalf.
            </p>
            <p style={styles.textSpaced}>
              We confirm that we have not availed trade credit against above mentioned import
              transaction from any other bank or financial institution.
            </p>
            <p style={styles.textSpaced}>
              We also confirm that the period of trade credit is within our operating cycle.
            </p>
            <p style={styles.textSpaced}>
              We hereby authorize you to debit our following account number with
              you 0138084000000004 towards repayment of trade credit together
              with interest and other charges on maturity.
            </p>
            <p style={styles.textSpaced}>Thanking you,</p>
            <p style={styles.signoff}>BEST TIMBERS</p>
            <p style={styles.partner}>PARTNER</p>
            <p style={styles.partner}>Encl: Form A1</p>
          </div>

          <FormFooter />
        </div>
      </div>


      {/* ── Download Button ─────────────────────────────────────────────── */}
      <div className="d-flex justify-content-center py-2">
        <button
          type="button"
          id="sblc-download-btn"
          className="btn btn-orange text-uppercase fw-bold px-4 py-2"
          onClick={handleDownload}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Generating PDF...
            </>
          ) : (
            <>📥 Download SBLC</>
          )}
        </button>
      </div>
    </>
  );
};

export default SblcSection;
